import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

// Project data - With actual screenshots and new projects
const projects = [
  {
    id: "intlrr",
    title: "INTLRR",
    category: "EdTech Platform",
    year: "2024",
    image: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/cq7rzh1j_Intlrr.PNG",
    description: "Finance education platform with cohort-based learning programs",
    size: "large"
  },
  {
    id: "paingoriya",
    title: "Paingoriya & Associates",
    category: "CA Firm Website",
    year: "2024",
    image: "https://customer-assets.emergentagent.com/job_aakaar-studio/artifacts/okzz4rtw_paingoriya%26associates.PNG",
    description: "Professional chartered accountants firm digital presence",
    size: "medium"
  },
  {
    id: "alvron-partners",
    title: "Alvron Partners",
    category: "Investment Banking",
    year: "2024",
    image: "https://images.unsplash.com/photo-1761735486587-bcac08b15c79?w=800&q=80",
    description: "Boutique investment banking firm website",
    size: "medium"
  },
  {
    id: "pyqguide",
    title: "PYQ Guide",
    category: "Exam Prep Platform",
    year: "2024",
    image: "https://images.unsplash.com/photo-1756032433560-56547efed550?w=800&q=80",
    description: "Government exam preparation platform",
    size: "large"
  },
  {
    id: "luxe-brand",
    title: "Luxe Brand Identity",
    category: "Brand Identity",
    year: "2024",
    image: "https://images.unsplash.com/photo-1759563869318-54434b2abc68?w=800&q=80",
    description: "Luxury fashion brand identity design",
    size: "medium"
  },
  {
    id: "tech-startup",
    title: "Tech Startup Landing",
    category: "Landing Page",
    year: "2024",
    image: "https://images.unsplash.com/photo-1720135885458-0af8193c4a0f?w=800&q=80",
    description: "High-converting SaaS landing page",
    size: "medium"
  }
];

const categories = ["All", "EdTech Platform", "CA Firm Website", "Investment Banking", "Exam Prep Platform", "Brand Identity", "Landing Page"];

export default function Work() {
  return (
    <div className="bg-[#050505] min-h-screen pt-24 md:pt-32" data-testid="work-page">
      {/* Header */}
      <section className="section-padding pb-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-xs mb-4">
              Our Portfolio
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              Selected <span className="italic">Work</span>
            </h1>
            <p className="text-[#A1A1A1] text-base md:text-lg max-w-2xl">
              A curated collection of our finest projects, showcasing our commitment 
              to design excellence and brand elevation.
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex flex-wrap gap-3 mt-12"
          >
            {categories.map((category, index) => (
              <button
                key={category}
                className={`text-xs uppercase tracking-widest px-4 py-2 transition-all duration-300 ${
                  index === 0
                    ? "text-[#050505] bg-[#C5A059]"
                    : "text-[#A1A1A1] border border-white/10 hover:border-[#C5A059] hover:text-[#C5A059]"
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bento Grid Portfolio */}
      <section className="section-padding pt-0" data-testid="portfolio-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className={`${
                  project.size === "large" ? "md:col-span-2 lg:col-span-2" : ""
                }`}
              >
                <Link
                  to={`/work/${project.id}`}
                  className="block group"
                  data-testid={`portfolio-item-${project.id}`}
                >
                  <motion.div 
                    className={`overflow-hidden bg-[#0F0F0F] border border-white/5 group-hover:border-[#C5A059]/30 transition-colors duration-500 ${
                      project.size === "large" ? "aspect-[16/9]" : "aspect-[4/3]"
                    }`}
                    whileHover="hover"
                  >
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top filter grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                      variants={{
                        hover: { scale: 1.05 }
                      }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </motion.div>
                  <div className="mt-6 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-4 mb-2">
                        <p className="text-[#A1A1A1] text-xs uppercase tracking-widest">
                          {project.category}
                        </p>
                        <span className="text-[#A1A1A1]/50 text-xs">{project.year}</span>
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl text-white group-hover:text-[#C5A059] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-[#A1A1A1] text-sm mt-2 hidden md:block">
                        {project.description}
                      </p>
                    </div>
                    <motion.div
                      whileHover={{ x: 4, y: -4 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowUpRight 
                        size={24} 
                        className="text-[#A1A1A1] group-hover:text-[#C5A059] transition-colors flex-shrink-0" 
                      />
                    </motion.div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding border-t border-white/5">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[#A1A1A1] uppercase tracking-[0.3em] text-xs mb-6">
              Like what you see?
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white mb-8">
              Let's create something <span className="italic text-[#C5A059]">extraordinary</span>
            </h2>
            <a
              href="https://wa.me/918006521008"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-3"
              data-testid="work-cta-whatsapp"
            >
              Start Your Project
              <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
